import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { DateRangePicker, FocusedInputShape } from "react-dates";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import ContentWrapper from "../../components/content-wrapper";
import { useProperty } from "../../components/property-provider";
import { SectionHeading } from "../../components/section-heading";
import Selector from "../../components/selector";
import { useConfig } from "../../lib/config";
import { useHttpClient } from "../../lib/http";
import { createFileDownloadFromRawData } from "../../lib/supportFunctions";
import { WaitingListEventType } from "./domain/WaitingListEventType.enum";
import { useWaitingListReportApi } from "./infrastructure/WaitingList.api";
import { waitingListReportMessages } from "./waitingListReport.messages";

type DateRange = {
  startDate?: moment.Moment;
  endDate?: moment.Moment;
};

const DateRangePickerWrapper = styled.div<{ hasError: boolean }>`
  .DateRangePicker {
    border-radius: 5px;
  }

  .DateRangePickerInput {
    border-radius: 5px;
    border-color: ${p => (p.hasError ? "red" : "hsl(0, 0%, 80%)")};
    width: 240px;
    padding: 2px;
  }

  .DateInput {
    border-radius: 5px;
  }

  .DateInput_input {
    font-size: 13px;
    font-weight: normal;
  }

  .DateRangePickerInput_clearDates_svg {
    width: auto;
  }
`;

export function WaitingListReport() {
  const [count, setCount] = useState("-");
  const [eventType, setEventType] = useState(WaitingListEventType.ITEM_CREATED);
  const [dates, setDates] = useState<DateRange | undefined>();
  const [datePickerFocusState, setDatePickerFocusState] = useState<FocusedInputShape | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasDatesError, setHasDatesError] = useState(false);

  const intl = useIntl();
  const config = useConfig();
  const { ticker } = useProperty();
  const httpClient = useHttpClient(config.waitingListApiEndpoint);
  const { getWaitingListCount, getWaitingListReport } = useWaitingListReportApi(httpClient);

  const eventTypes = useMemo(
    () => [
      {
        value: WaitingListEventType.ITEM_CREATED,
        label: intl.formatMessage(waitingListReportMessages.typeEventItemCreated),
      },
      {
        value: WaitingListEventType.EMAIL_SENT,
        label: intl.formatMessage(waitingListReportMessages.typeEventEmailSent),
      },
    ],
    [intl]
  );

  const onDatesChange = useCallback(
    ({
      startDate,
      endDate,
    }: {
      endDate: moment.Moment | null;
      startDate: moment.Moment | null;
    }) => {
      if (startDate && endDate) {
        setHasDatesError(false);
      }

      setDates({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
    },
    []
  );

  const onExport = useCallback(async () => {
    const { startDate, endDate } = dates ?? { startDate: undefined, endDate: undefined };
    if (!startDate || !endDate) {
      setHasDatesError(true);
      return;
    }

    setIsLoading(true);
    const csv = await getWaitingListReport(
      ticker,
      eventType,
      startDate.toDate(),
      endDate.toDate()
    ).finally(() => setIsLoading(false));
    createFileDownloadFromRawData(csv, "waiting-list-report.csv");
  }, [dates, eventType, getWaitingListReport, ticker]);

  useEffect(() => {
    (async () => {
      setCount(String(await getWaitingListCount(ticker)));
    })();
  }, [getWaitingListCount, ticker]);

  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...waitingListReportMessages.title} />
      </SectionHeading>
      <div className="flex flex-col items-start gap-4 p-4">
        <Card>
          <Card.Body className="flex flex-col gap-4 items-center justify-center">
            <div className="flex flex-col items-center gap-1">
              <span className="font-semibold leading-[15px]">
                <FormattedMessage {...waitingListReportMessages.countTitle} />
              </span>
              <span>
                <FormattedMessage {...waitingListReportMessages.countTooltip} />
              </span>
            </div>
            <span className="text-2xl font-bold">{count}</span>
          </Card.Body>
        </Card>
        <Card className="w-full">
          <Card.Body className="flex gap-3 items-center">
            <Selector
              className="w-60"
              defaultValue={eventTypes[0]}
              onChange={(event: { label: string; value: WaitingListEventType }) =>
                setEventType(event.value)
              }
              options={eventTypes}
              placeholder={
                <FormattedMessage {...waitingListReportMessages.typeFilterPlaceholder} />
              }
              portal={true}
            />
            <DateRangePickerWrapper hasError={hasDatesError}>
              <DateRangePicker
                startDate={dates?.startDate ?? null}
                startDatePlaceholderText={intl.formatMessage(waitingListReportMessages.startDate)}
                startDateId={"eventStart"}
                endDate={dates?.endDate ?? null}
                endDatePlaceholderText={intl.formatMessage(waitingListReportMessages.endDate)}
                endDateId={"eventEnd"}
                focusedInput={datePickerFocusState}
                onFocusChange={setDatePickerFocusState}
                isOutsideRange={() => false}
                displayFormat="DD/MM/YYYY"
                onDatesChange={onDatesChange}
                minimumNights={0}
                small
                showClearDates
                withPortal
              />
            </DateRangePickerWrapper>
            <Button onClick={onExport} disabled={isLoading}>
              <FormattedMessage {...waitingListReportMessages.export} />
            </Button>
          </Card.Body>
        </Card>
      </div>
    </ContentWrapper>
  );
}
